import React, { useEffect, useState, useCallback, useMemo } from "react";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@finance/components/ui/dialog";
import { api, fmtNum, openPdf } from "@finance/lib/api";
import { toast } from "sonner";
import { Plus, Warning, PencilSimple, MagnifyingGlass, X, Lock, Truck, Trash, FileArrowDown } from "@phosphor-icons/react";
import { EfdTimeField } from "@finance/components/EfdTimeField";

const STATUS_STYLE = {
  Open: "bg-amber-50 text-amber-700 border-amber-200",
  Received: "bg-blue-50 text-blue-700 border-blue-200",
  Closed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const STATUS_HINT = {
  Open: "In transit — no GRV posted yet",
  Received: "Goods arrived (GRV posted for all linked POs)",
  Closed: "Fully received & all costs settled (GIT = 0)",
};

// Shipment coverage on a PO line: received (arrived) vs in-transit (allocated, not yet received) vs still-to-ship.
function CoverageBar({ ordered, received, inTransit }) {
  const ord = Number(ordered) || 0;
  if (ord <= 0) return null;
  const rec = Math.min(Number(received) || 0, ord);
  const tr = Math.min(Number(inTransit) || 0, Math.max(ord - rec, 0));
  const remaining = Math.max(ord - rec - tr, 0);
  const pct = (v) => `${(v / ord) * 100}%`;
  const trim = (n) => Number(n.toFixed(3));
  return (
    <div className="mt-1 pl-5" data-testid="coverage-bar">
      <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        {rec > 0 && <div className="h-full bg-emerald-500" style={{ width: pct(rec) }} title={`Received ${trim(rec)}`} />}
        {tr > 0 && <div className="h-full bg-[#1B4383]" style={{ width: pct(tr) }} title={`In transit ${trim(tr)}`} />}
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[10px]">
        <span className="font-medium text-gray-500">shipped {trim(rec + tr)} of {trim(ord)}</span>
        {rec > 0 && <span className="text-emerald-600">● received {trim(rec)}</span>}
        {tr > 0 && <span className="text-[#1B4383]">● in transit {trim(tr)}</span>}
        {remaining > 0 && <span className="text-amber-600">● {trim(remaining)} to ship</span>}
      </div>
    </div>
  );
}

export default function Shipments() {
  const [rows, setRows] = useState([]);
  const [labelMap, setLabelMap] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [dupes, setDupes] = useState([]);
  // Picker state
  const [cands, setCands] = useState([]);
  const [sel, setSel] = useState({});        // { po_id: [line_index, ...] }
  const [qtys, setQtys] = useState({});      // { `${po_id}:${line_index}`: shipped_qty }
  const [qtyErr, setQtyErr] = useState({});  // { `${po_id}:${line_index}`: error message }
  const [openPos, setOpenPos] = useState([]); // POs revealed in the working area (order preserved)
  const [search, setSearch] = useState("");
  // Freight (shipment-level)
  const [suppliers, setSuppliers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [editFreight, setEditFreight] = useState([]);
  const FF0 = { supplier_id: "", supplier_name: "", amount: "", currency: "TZS", date: "", bank_id: "", fx_rate: "", locally_registered: false, efd_code: "", efd_time: "" };
  const [ff, setFf] = useState(FF0);
  const [settleFor, setSettleFor] = useState(null);
  const [settleBank, setSettleBank] = useState("");
  const [settleDate, setSettleDate] = useState("");
  const [unassigned, setUnassigned] = useState({ lines: [], freight_without_lines: [], total_goods_value: 0 });

  const load = useCallback(async () => {
    const r = await api.get("/shipments");
    setRows(r.data || []);
    try { const u = await api.get("/shipments/unassigned-lines"); setUnassigned(u.data || { lines: [], freight_without_lines: [] }); } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Lightweight label map for the table (PO id → PO number).
    api.get("/transactions").then((r) => {
      const m = {};
      (r.data || []).forEach((t) => { m[t.id] = t.lx_po_no || (t.id || "").slice(0, 8); });
      setLabelMap(m);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => {});
    api.get("/bank-accounts").then((r) => setBanks(r.data || [])).catch(() => {});
  }, []);

  const poLabel = (tid) => labelMap[tid] || (tid || "").slice(0, 8);
  const candById = useCallback((id) => cands.find((c) => c.po_id === id), [cands]);
  const selCount = useMemo(() => Object.values(sel).reduce((a, b) => a + (b?.length || 0), 0), [sel]);

  const loadCands = async (sid) => {
    try {
      const r = await api.get("/shipments/po-candidates", { params: sid ? { shipment_id: sid } : {} });
      const data = r.data || [];
      setCands(data);
      const s = {}; const op = []; const qm = {};
      data.forEach((po) => {
        const chosen = po.lines.filter((l) => l.selected).map((l) => l.line_index);
        if (chosen.length) { s[po.po_id] = chosen; op.push(po.po_id); }
        po.lines.forEach((l) => { if (l.selected) qm[`${po.po_id}:${l.line_index}`] = String(l.ship_qty ?? l.qty ?? ""); });
      });
      setSel(s); setOpenPos(op); setQtys(qm); setQtyErr({});
    } catch { setCands([]); setSel({}); setOpenPos([]); setQtys({}); setQtyErr({}); }
  };

  const openNew = () => { setEditing(null); setLabel(""); setNotes(""); setDupes([]); setSearch(""); setEditFreight([]); setFf(FF0); setOpen(true); loadCands(null); };
  const openEdit = (s) => { setEditing(s.id); setLabel(s.label); setNotes(s.notes || ""); setDupes([]); setSearch(""); setEditFreight(s.freight || []); setFf(FF0); setOpen(true); loadCands(s.id); };

  const checkDup = async (val) => {
    if (!val.trim()) { setDupes([]); return; }
    try { const r = await api.get("/shipments/check-duplicate", { params: { label: val } }); setDupes(r.data.candidates.filter((c) => c.id !== editing)); }
    catch { setDupes([]); }
  };

  const revealPo = (po_id) => {
    setOpenPos((prev) => (prev.includes(po_id) ? prev : [...prev, po_id]));
    setSearch("");
  };
  const toggleLine = (po_id, li, ordered) => {
    setSel((prev) => {
      const cur = prev[po_id] || [];
      const next = cur.includes(li) ? cur.filter((x) => x !== li) : [...cur, li];
      return { ...prev, [po_id]: next };
    });
    setQtys((q) => (q[`${po_id}:${li}`] != null ? q : { ...q, [`${po_id}:${li}`]: String(ordered ?? "") }));
  };
  const setQty = (po_id, li, v, max) => {
    const key = `${po_id}:${li}`;
    if (max != null && v !== "" && Number(v) > Number(max)) {
      setQtyErr((e) => ({ ...e, [key]: `Only ${max} remaining on this PO line` }));
      setQtys((q) => ({ ...q, [key]: String(max) }));  // cap at the remaining quantity
      return;
    }
    setQtyErr((e) => { const n = { ...e }; delete n[key]; return n; });
    setQtys((q) => ({ ...q, [key]: v }));
  };
  const selectAllQty = (po) => {
    const lines = po.lines.filter((l) => l.selectable);
    setSel((prev) => ({ ...prev, [po.po_id]: lines.map((l) => l.line_index) }));
    setQtys((q) => { const n = { ...q }; lines.forEach((l) => { n[`${po.po_id}:${l.line_index}`] = String(l.remaining ?? l.ordered ?? l.qty ?? ""); }); return n; });
  };
  const removePo = (po_id) => {
    setSel((prev) => { const n = { ...prev }; delete n[po_id]; return n; });
    setOpenPos((prev) => prev.filter((x) => x !== po_id));
  };

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return cands.filter((c) =>
      !openPos.includes(c.po_id) &&
      ((c.lx_po_no || "").toLowerCase().includes(q) || (c.supplier_name || "").toLowerCase().includes(q))
    ).slice(0, 12);
  }, [search, cands, openPos]);

  const addFreight = async () => {
    const amt = Number(ff.amount);
    if (!amt || amt <= 0) { toast.error("Enter a freight amount greater than zero."); return; }
    try {
      const payload = { supplier_id: ff.supplier_id || null, supplier_name: ff.supplier_name || null, amount: amt,
        currency: ff.currency || "TZS", fx_rate: ff.fx_rate ? Number(ff.fx_rate) : null, date: ff.date || null, bank_id: ff.bank_id || null,
        locally_registered: !!ff.locally_registered, efd_code: ff.efd_code || null, efd_time: ff.efd_time || null };
      const r = await api.post(`/shipments/${editing}/freight`, payload);
      setEditFreight((prev) => [...prev, r.data]);
      setFf(FF0);
      toast.success("Freight added and apportioned across the shipment's PO lines.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add freight."); }
  };
  const delFreight = async (fid) => {
    try { await api.delete(`/shipments/${editing}/freight/${fid}`); setEditFreight((prev) => prev.filter((f) => f.id !== fid)); toast.success("Freight removed."); load(); }
    catch { toast.error("Could not remove freight."); }
  };
  const doSettle = async (fid) => {
    if (!settleBank) { toast.error("Choose the bank to settle from."); return; }
    try {
      await api.patch(`/shipments/${editing}/freight/${fid}`, { paid: true, paid_bank_id: settleBank, paid_date: settleDate || null });
      setEditFreight((prev) => prev.map((f) => (f.id === fid ? { ...f, paid: true, paid_bank_id: settleBank, paid_date: settleDate } : f)));
      setSettleFor(null); setSettleBank(""); setSettleDate("");
      toast.success("Freight settled from bank."); load();
    } catch { toast.error("Could not settle freight."); }
  };

  const save = async (force = false) => {
    if (!label.trim()) { toast.error("Enter the shipment label your logistics team uses."); return; }
    if (Object.keys(qtyErr).length) { toast.error("Some line quantities exceed the remaining amount — fix them first."); return; }
    const po_lines = Object.entries(sel)
      .filter(([, idxs]) => idxs && idxs.length)
      .map(([po_id, idxs]) => ({ po_id, line_indexes: idxs,
        line_qtys: idxs.reduce((m, li) => { const v = Number(qtys[`${po_id}:${li}`]); if (v > 0) m[String(li)] = v; return m; }, {}) }));
    const payload = { label: label.trim(), po_lines, notes: notes || null, force };
    try {
      if (editing) await api.patch(`/shipments/${editing}`, payload);
      else await api.post("/shipments", payload);
      toast.success(editing ? "Shipment updated." : "Shipment created.");
      setOpen(false); setDupes([]); load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.candidates) {
        setDupes(det.candidates); toast.warning("A near-duplicate shipment exists — select it, or confirm to create anyway.");
      } else { toast.error(typeof det === "string" ? det : "Could not save shipment."); }
    }
  };

  return (
    <Layout>
      <PageHeader title="Shipments (Goods in Transit)" subtitle="Finance-owned register keying the GIT sub-ledger — link shipments to specific PO line items; status is derived automatically"
        actions={<Button data-testid="shipment-new-btn" onClick={openNew} className="gap-2 bg-[#1B4383] hover:bg-[#122E5C]"><Plus size={16} /> New Shipment</Button>}
      />
      <div className="p-8">
        {(unassigned.lines?.length > 0 || unassigned.freight_without_lines?.length > 0) && (
          <div className="mb-5 rounded-sm border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="unassigned-banner">
            <div className="flex items-center gap-2 font-medium"><Warning size={16} /> Import value sitting outside a shipment sub-ledger</div>
            {unassigned.lines?.length > 0 && <p className="mt-1">{unassigned.lines.length} import PO line(s) totalling <span className="lx-num font-semibold">{fmtNum(unassigned.total_goods_value || 0, "TZS")}</span> aren't linked to any shipment — assign them so their goods value (and freight) capitalize to GIT.</p>}
            {unassigned.freight_without_lines?.length > 0 && <p className="mt-1">{unassigned.freight_without_lines.length} shipment(s) carry freight but have no linked PO lines — that freight can't apportion yet.</p>}
            {unassigned.lines?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{unassigned.lines.slice(0, 15).map((l, idx) => <span key={idx} className="rounded-sm border border-amber-200 bg-white px-1.5 py-0.5 text-xs" data-testid={`unassigned-chip-${idx}`}>{l.lx_po_no} · {l.description}</span>)}</div>}
          </div>
        )}
        <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
          <table className="w-full text-sm" data-testid="shipments-table">
            <thead><tr className="lx-label border-b border-gray-100 text-gray-400">
              <th className="px-5 py-2 text-left">Shipment</th><th className="px-5 py-2 text-left">Linked POs</th>
              <th className="px-5 py-2 text-left">Status</th><th className="px-5 py-2 text-right">GIT balance (TZS)</th>
              <th className="px-5 py-2 text-left">Created</th><th className="px-5 py-2 text-right"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-gray-400">No shipments yet. Create one at booking time and link its PO line item(s).</td></tr>}
              {rows.map((s) => (
                <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`shipment-row-${s.id}`}>
                  <td className="px-5 py-2 font-medium text-gray-900">{s.label}</td>
                  <td className="px-5 py-2 lx-num text-gray-600">{(s.po_ids || []).map(poLabel).join(", ") || "—"}</td>
                  <td className="px-5 py-2">
                    <span className={`inline-block rounded-sm border px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[s.status] || ""}`} title={STATUS_HINT[s.status]} data-testid={`shipment-status-${s.id}`}>{s.status}</span>
                  </td>
                  <td className={`px-5 py-2 text-right lx-num font-semibold ${(s.git_balance || 0) > 0 ? "text-amber-700" : (s.git_balance || 0) < 0 ? "text-red-600" : "text-gray-400"}`} data-testid={`shipment-bal-${s.id}`}>{fmtNum(s.git_balance || 0, "TZS")}</td>
                  <td className="px-5 py-2 text-gray-500">{(s.created_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-2 text-right whitespace-nowrap">
                    <button className="mr-3 text-gray-300 hover:text-[#1B4383]" title="Landed-cost statement (PDF)" data-testid={`shipment-landed-pdf-${s.id}`} onClick={() => openPdf(`pdf/shipment-landed-cost/${s.id}`)}><FileArrowDown size={15} /></button>
                    <button className="text-gray-300 hover:text-[#1B4383]" title="Edit" data-testid={`shipment-edit-${s.id}`} onClick={() => openEdit(s)}><PencilSimple size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-gray-400">Status is auto-derived: <b>Open</b> = in transit (no GRV yet) · <b>Received</b> = GRV posted for every linked PO · <b>Closed</b> = GIT balance nets to zero (all costs settled). A positive balance = goods received, costs not yet settled; negative = costs incurred, goods not yet received.</p>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setDupes([]); }}>
        <DialogContent data-testid="shipment-dialog" className="flex max-h-[90vh] flex-col max-w-2xl">
          <DialogHeader className="shrink-0"><DialogTitle className="font-display">{editing ? "Edit Shipment" : "New Shipment"}</DialogTitle>
            <DialogDescription>Use the exact label your logistics team uses. Add PO line items (a PO can be split across shipments).</DialogDescription>
          </DialogHeader>
          <div className="flex-1 space-y-4 overflow-y-auto px-1">
            <div><Label className="lx-label">Shipment label (logistics naming)</Label>
              <Input data-testid="shipment-label-input" value={label} onChange={(e) => setLabel(e.target.value)} onBlur={(e) => checkDup(e.target.value)} className="mt-1" placeholder="e.g. MSKU7841233 / SHP-014" />
            </div>
            {dupes.length > 0 && (
              <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700" data-testid="shipment-dup-warning">
                <div className="mb-1 flex items-center gap-1 font-medium"><Warning size={15} /> Similar shipment(s) already exist:</div>
                <ul className="ml-5 list-disc">{dupes.map((c) => <li key={c.id}>{c.label} <span className="text-amber-500">({Math.round(c.similarity * 100)}% match)</span></li>)}</ul>
              </div>
            )}

            <div>
              <Label className="lx-label">Linked import PO line items</Label>
              <div className="relative mt-1">
                <MagnifyingGlass size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input data-testid="shipment-po-search" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" placeholder="Search a PO number or supplier to add…" />
                {searchResults.length > 0 && (
                  <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-sm border border-gray-200 bg-white shadow-lg" data-testid="shipment-po-search-results">
                    {searchResults.map((c) => (
                      <button key={c.po_id} type="button" data-testid={`shipment-po-option-${c.po_id}`} onClick={() => revealPo(c.po_id)}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50">
                        <span className="lx-num font-medium text-gray-800">{c.lx_po_no}</span>
                        {c.supplier_name && <span className="text-gray-400">{c.supplier_name}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {search.trim() && searchResults.length === 0 && (
                  <div className="absolute z-20 mt-1 w-full rounded-sm border border-gray-200 bg-white px-3 py-2 text-sm text-gray-400 shadow-lg">No matching import PO with available lines.</div>
                )}
              </div>

              <div className="mt-3 space-y-3" data-testid="shipment-selected-pos">
                {openPos.length === 0 && <div className="rounded-sm border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-400">No PO lines added yet. Search above to add a PO and pick its lines.</div>}
                {openPos.map((pid) => {
                  const po = candById(pid);
                  if (!po) return null;
                  const chosen = sel[pid] || [];
                  return (
                    <div key={pid} className="rounded-sm border border-gray-200" data-testid={`shipment-po-card-${pid}`}>
                      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-3 py-2">
                        <div className="text-sm"><span className="lx-num font-semibold text-gray-800">{po.lx_po_no}</span>{po.supplier_name && <span className="ml-2 text-gray-400">{po.supplier_name}</span>}</div>
                        <div className="flex items-center gap-2">
                          <button type="button" className="rounded border border-[#1B4383] px-2 py-0.5 text-[10px] font-semibold text-[#1B4383] hover:bg-[#1B4383]/10" data-testid={`shipment-selectall-${pid}`} onClick={() => selectAllQty(po)}>Select all qty</button>
                          <button type="button" className="text-gray-300 hover:text-red-500" data-testid={`shipment-po-remove-${pid}`} onClick={() => removePo(pid)}><X size={15} /></button>
                        </div>
                      </div>
                      <div className="divide-y divide-gray-50">
                        {po.lines.map((l) => {
                          const checked = chosen.includes(l.line_index);
                          if (!l.selectable) {
                            return (
                              <div key={l.line_index} className="px-3 py-1.5 text-gray-400" title={`Already in shipment ${l.assigned_shipment}`}>
                                <div className="flex items-center gap-2 text-sm">
                                  <Lock size={13} />
                                  <span className="flex-1">{l.description}</span>
                                  <span className="text-xs italic">in {l.assigned_shipment}</span>
                                </div>
                                <CoverageBar ordered={l.ordered ?? l.qty} received={l.received} inTransit={l.in_transit} />
                              </div>
                            );
                          }
                          return (
                            <div key={l.line_index} className="px-3 py-1.5 hover:bg-gray-50">
                              <div className="flex items-center gap-2 text-sm">
                              <input type="checkbox" checked={checked} onChange={() => toggleLine(pid, l.line_index, l.remaining ?? l.ordered ?? l.qty)} data-testid={`shipment-line-${pid}-${l.line_index}`} />
                              <span className={`flex-1 cursor-pointer ${checked ? "font-medium text-gray-800" : "text-gray-600"}`} onClick={() => toggleLine(pid, l.line_index, l.remaining ?? l.ordered ?? l.qty)}>{l.description}{l.also_in && <span className="ml-2 text-[10px] italic text-gray-400">· also in {l.also_in}</span>}</span>
                              {checked
                                ? <span className="flex flex-col items-end text-xs text-gray-400">
                                    <span className="flex items-center gap-1">qty <input type="number" min="0" max={l.remaining ?? l.ordered ?? l.qty} className={`w-20 rounded border px-1.5 py-0.5 text-xs lx-num ${qtyErr[`${pid}:${l.line_index}`] ? "border-red-400 bg-red-50" : "border-gray-200"}`} value={qtys[`${pid}:${l.line_index}`] ?? ""} onChange={(e) => setQty(pid, l.line_index, e.target.value, l.remaining ?? l.ordered ?? l.qty)} data-testid={`shipment-qty-${pid}-${l.line_index}`} /> {(l.remaining ?? l.ordered) != null && <span className="text-gray-300">of {l.remaining ?? l.ordered}</span>}</span>
                                    {qtyErr[`${pid}:${l.line_index}`] && <span className="mt-0.5 text-red-500" data-testid={`shipment-qty-err-${pid}-${l.line_index}`}>{qtyErr[`${pid}:${l.line_index}`]}</span>}
                                  </span>
                                : (l.qty != null && <span className="lx-num text-xs text-gray-400">qty {l.qty}</span>)}
                              </div>
                              <CoverageBar ordered={l.ordered ?? l.qty} received={l.received} inTransit={l.in_transit} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              {selCount > 0 && <div className="mt-2 text-xs text-gray-500" data-testid="shipment-sel-count">{selCount} line item(s) selected across {Object.values(sel).filter((v) => v?.length).length} PO(s)</div>}
            </div>

            <div><Label className="lx-label">Notes</Label><Input data-testid="shipment-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" /></div>

            {editing && (
              <div className="rounded-sm border border-gray-200 p-3" data-testid="shipment-freight-panel">
                <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700"><Truck size={15} /> Freight (one invoice per shipment → Dr GIT)</div>
                {editFreight.length > 0 && (
                  <div className="mb-2 divide-y divide-gray-50 rounded-sm border border-gray-100">
                    {editFreight.map((f) => (
                      <div key={f.id} className="px-2 py-1.5 text-sm" data-testid={`freight-row-${f.id}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">{f.supplier_name || "—"} · <span className="lx-num">{fmtNum(f.amount || 0, f.currency || "TZS")}</span>{f.currency && f.currency !== "TZS" ? ` ${f.currency}` : ""}{f.date ? ` · ${f.date}` : ""}{f.efd_code ? <span className="ml-1 text-gray-400">· EFD {f.efd_code}</span> : ""}</span>
                          <span className="flex items-center gap-2">
                            {f.paid
                              ? <span className="rounded-sm border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-xs text-emerald-700" data-testid={`freight-paid-${f.id}`}>Paid</span>
                              : (<>
                                  <span className="rounded-sm border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700" data-testid={`freight-unpaid-${f.id}`}>Unpaid · AP</span>
                                  <button className="text-xs font-medium text-[#1B4383] hover:underline" data-testid={`freight-settle-${f.id}`} onClick={() => { setSettleFor(f.id); setSettleBank(""); setSettleDate(""); }}>Settle</button>
                                </>)}
                            <button className="text-xs font-medium text-[#1B4383] hover:underline" data-testid={`freight-po-${f.id}`} title="Freight PO (FPO) — document only, no GL" onClick={() => openPdf(`/shipments/${editing}/freight/${f.id}/po`)}>{f.fpo_no ? `Download PO ${f.fpo_no}` : "Issue PO"}</button>
                            <button className="text-gray-300 hover:text-red-500" data-testid={`freight-del-${f.id}`} onClick={() => delFreight(f.id)}><Trash size={14} /></button>
                          </span>
                        </div>
                        {settleFor === f.id && (
                          <div className="mt-1 flex items-center gap-2" data-testid={`freight-settle-form-${f.id}`}>
                            <Select value={settleBank} onValueChange={setSettleBank}>
                              <SelectTrigger data-testid={`freight-settle-bank-${f.id}`} className="h-8 w-44"><SelectValue placeholder="Settle from bank" /></SelectTrigger>
                              <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name || b.bank_name || b.currency}</SelectItem>)}</SelectContent>
                            </Select>
                            <Input type="date" value={settleDate} onChange={(e) => setSettleDate(e.target.value)} className="h-8 w-36" data-testid={`freight-settle-date-${f.id}`} />
                            <Button size="sm" className="h-8 bg-[#1B4383] hover:bg-[#122E5C]" data-testid={`freight-settle-confirm-${f.id}`} onClick={() => doSettle(f.id)}>Confirm</Button>
                            <button className="text-xs text-gray-400 hover:underline" onClick={() => setSettleFor(null)}>Cancel</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <Select value={ff.supplier_id || ""} onValueChange={(v) => { const s = suppliers.find((x) => x.id === v); setFf({ ...ff, supplier_id: v, supplier_name: s?.name || "" }); }}>
                    <SelectTrigger data-testid="freight-supplier" className="h-9"><SelectValue placeholder="Freight supplier" /></SelectTrigger>
                    <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input data-testid="freight-amount" type="number" placeholder="Amount" value={ff.amount} onChange={(e) => setFf({ ...ff, amount: e.target.value })} className="h-9" />
                  <Input data-testid="freight-currency" placeholder="Currency" value={ff.currency} onChange={(e) => setFf({ ...ff, currency: e.target.value.toUpperCase() })} className="h-9" />
                  <Input data-testid="freight-date" type="date" value={ff.date} onChange={(e) => setFf({ ...ff, date: e.target.value })} className="h-9" />
                  {ff.currency && ff.currency !== "TZS" && <Input data-testid="freight-fx" type="number" placeholder="FX rate → TZS" value={ff.fx_rate} onChange={(e) => setFf({ ...ff, fx_rate: e.target.value })} className="h-9" />}
                  <Select value={ff.bank_id || "__ap__"} onValueChange={(v) => setFf({ ...ff, bank_id: v === "__ap__" ? "" : v })}>
                    <SelectTrigger data-testid="freight-bank" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__ap__">Accrue to AP (unpaid)</SelectItem>
                      {banks.map((b) => <SelectItem key={b.id} value={b.id}>Paid from {b.name || b.bank_name || b.currency}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                  <input type="checkbox" checked={!!ff.locally_registered} onChange={(e) => setFf({ ...ff, locally_registered: e.target.checked })} data-testid="freight-efd-toggle" />
                  Freight supplier is locally (EFD) registered
                </label>
                {ff.locally_registered && (
                  <div className="mt-1 grid grid-cols-2 gap-2">
                    <Input data-testid="freight-efd-code" placeholder="EFD verification code" value={ff.efd_code} onChange={(e) => setFf({ ...ff, efd_code: e.target.value })} className="h-9" />
                    <EfdTimeField testid="freight-efd-time" value={ff.efd_time} onChange={(v) => setFf({ ...ff, efd_time: v })} />
                  </div>
                )}
                <Button size="sm" variant="outline" className="mt-2 gap-1" data-testid="freight-add-btn" onClick={addFreight}><Plus size={13} /> Add freight</Button>
                <p className="mt-1 text-xs text-gray-400">Freight accrues Dr GIT / Cr AP, auto-apportioned across this shipment's linked PO lines. Mark paid to post the bank settlement. EFD fields apply only when a local supplier bills the freight.</p>
              </div>
            )}
          </div>
          <DialogFooter className="shrink-0 border-t border-gray-100 pt-3">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            {dupes.length > 0
              ? <Button data-testid="shipment-force-save" className="bg-amber-600 hover:bg-amber-700" onClick={() => save(true)}>Save anyway</Button>
              : <Button data-testid="shipment-save-btn" className="bg-[#1B4383] hover:bg-[#122E5C]" onClick={() => save(false)}>{editing ? "Save Changes" : "Create Shipment"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
